import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { concatMap, EMPTY, Observable, of, Subject, switchMap, tap, withLatestFrom } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { PrivaNotificationsService } from '@priva/components/notifications';

import { notificationWarningTimeout } from 'app/shared/constants';
import { selectIsScannedCodeValid } from 'app/state/scanned-codes/scanned-codes.selectors';

import { NfcReadEvent, ScannedCodeType } from '../../models';
import { FeedbackMessageService, NfcReaderService } from '../../services';

export interface ScanNfcControllerState {
    allowedCodeTypes: ScannedCodeType[];
}

const initialState: ScanNfcControllerState = { allowedCodeTypes: [] };

@Injectable()
export class ScanNfcControllerStore extends ComponentStore<ScanNfcControllerState> {
    private store = inject(Store);
    feedbackMessageService = inject(FeedbackMessageService);
    private notificationService = inject(PrivaNotificationsService);

    private reader = new NfcReaderService();
    readerMalfunction$ = this.reader.readerMalfunction$;
    readEvent$ = new Subject<NfcReadEvent>();

    constructor() {
        super(initialState);
    }

    private allowedCodeTypes$ = this.select((state) => state.allowedCodeTypes);

    startReading = this.effect((trigger$: Observable<void>) => {
        return trigger$.pipe(
            tap(() => {
                this.reader.startReading();
            }),
            switchMap(() => this.reader.readEvent$),
            withLatestFrom(this.allowedCodeTypes$),
            concatMap(([readEvent, allowedCodeTypes]) => {
                if (readEvent.error) {
                    return of(readEvent);
                }

                return this.store.pipe(
                    selectIsScannedCodeValid({ scannedCode: readEvent.readText, allowedCodeTypes }),
                    take(1),
                    map((isScannedCodeValid) => ({
                        readText: readEvent.readText,
                        error: this.feedbackMessageService.getScanNotificationMessage(
                            isScannedCodeValid.error,
                        ),
                    })),
                );
            }),
            tap((readEvent) => {
                if (readEvent.error) {
                    this.showWarning(readEvent.error);
                }
                this.readEvent$.next(readEvent);
            }),
            catchError(() => EMPTY),
        );
    });

    private showWarning(message: string) {
        this.notificationService.toaster.warning(message, { timeout: notificationWarningTimeout });
    }

    stopReading() {
        this.reader.stopReading();
    }

    setAllowedCodeTypes = this.updater((state, allowedCodeTypes: ScannedCodeType[]) => {
        return {
            ...state,
            allowedCodeTypes,
        };
    });
}
